/**
 * AmitySocialHub - A component for displaying social hub
 *
 * @module views/components/AmitySocialHub
 * @memberof -Common
 */
import './AmitySocialHub.scss';
import '@amityco/ulta-ui-kit/dist/index.css';

import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import LoaderCircular from '@ulta/core/components/LoaderCircular/LoaderCircular';
import Video from '@ulta/core/components/Video/Video';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { isEmptyObject } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import * as utils from './AmitySocialHub';

const AMITY_EVENT = {
  EXPLORE_PAGE_LOADED: 'explorePageLoaded',
  ON_PAGE_LOADED: 'onPageLoaded',
  ANONYMOUS_NEEDS_LOGIN: 'anonymousNeedsLogin',
  PARENT_NEEDS_SCROLL_TO_TOP: 'parentNeedsScrollToTop'
};

/**
 * Represents a AmitySocialHub component
 *
 * @method
 * @param {AmitySocialHubProps} props - React properties passed from composition
 * @returns AmitySocialHub
 */
export const AmitySocialHub = ( props ) => {
  const { profileId, loginAction, invokeAction, fetchAuthTokenAction, authToken, authTokenRequest, post } = props;
  const [hasLoaded, setHasLoaded] = useState( false );
  const [dailyPrompt, setDailyPrompt] = useState( false );
  const [shouldShowLogin, setShouldShowLogin] = useState( false );
  const [authTokenProfileId, setAuthTokenProfileId] = useState( null );
  const [fetchAuthToken, setFetchAuthToken] = useState( false );

  const AmityKit = useAmityUIKit( { setDailyPrompt, setHasLoaded, setShouldShowLogin } );

  useEffect( () => {
    utils.handleLoginPrompt( { hasLoaded, loginAction, shouldShowLogin }, { invokeAction, setShouldShowLogin } );
  }, [hasLoaded, loginAction, shouldShowLogin, invokeAction] );

  useEffect( () => {
    utils.handleDailyPrompt( { setDailyPrompt, setShouldShowLogin } );
  }, [dailyPrompt] );

  useEffect( () => {
    if( !profileId ){
      setShouldShowLogin( true );
    }
  }, [profileId] );

  useEffect( () => {
    utils.handleAuthTokenRequest( { authToken, fetchAuthToken, fetchAuthTokenAction, authTokenProfileId },
      { invokeAction } );
  }, [fetchAuthToken, invokeAction] );

  useEffect( () => {
    utils.handleAuthTokenResponse( { authToken, authTokenRequest, fetchAuthToken }, { setFetchAuthToken } );
  }, [authTokenRequest, authToken] );

  const videoUrl = useMemo( () => getAmityVideoUrl( post ), [post] );

  const socialObjectJsonLd = useMemo( () => getAmitySocialObjectJsonLd( post ), [post] );

  const {
    amityClientKey,
    amityApiEndpoint,
    amityMessageOrigin,
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    allowUpdateDisplayName,
    defaultTab,
    postQueryString,
    groupQueryString,
    userQueryString
  } = useAppConfigContext();

  const getAuthToken = async() => {
    return authToken;
  };

  const ultaAmityConfig = {
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    privacyAndPolicyUrl,
    allowUpdateDisplayName,
    defaultTab,
    deeplink: {
      origin: `${amityMessageOrigin}/community`,
      groupQueryString,
      postQueryString,
      userQueryString
    }
  };

  if( !AmityKit || !profileId || !amityClientKey || !amityApiEndpoint ){
    return (
      <div className='AmitySocialHub' style={ { position: 'relative', minHeight: '100dvh' } }>
        <LoaderCircular />
      </div>
    );
  }

  // The Amity UI Kit components are now loaded and can be used safely.
  const { AmityUiKitProvider, AmityUiKitSocial } = AmityKit;

  return (
    <div className='AmitySocialHub'>
      { ( videoUrl || socialObjectJsonLd ) &&
      <div className='AmitySocialHub__seo'>
        { videoUrl && <Video videoUrl={ videoUrl } /> }
        { socialObjectJsonLd &&
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={ { __html: socialObjectJsonLd } }
        /> }
      </div>
      }
      { profileId && (
        <AmityUiKitProvider
          key={ profileId }
          apiKey={ amityClientKey }
          apiEndpoint={ amityApiEndpoint }
          userId={ profileId }
          apiRegion='us'
          ultaConfig={ ultaAmityConfig }
          getAuthToken={ getAuthToken }
          onRouteChange={ utils.handleRouteChange }
        >
          <AmityUiKitSocial />
        </AmityUiKitProvider> ) }
    </div>
  );
};

/**
 * Handles the authtoken response from DXL...triggers event that sends the authtoken back to the child iframe
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleAuthTokenResponse = ( data, methods ) => {
  const { authToken, fetchAuthToken } = data || {};
  const { setFetchAuthToken } = methods || {};

  // no need to process since auth token never requested
  if( !fetchAuthToken ){
    return;
  }

  setFetchAuthToken( false );
};

/**
 * Handles route changes triggered by the Amity UI Kit.
 * It updates the browser's URL with the new page's query string.
 * @param {string} page - The query string for the new page (e.g., '?page=2').
 */
export const handleRouteChange = ( page ) => {
  if( page === '' ){
    return;
  }
  const url = new URL( window.location.href );
  url.search = `${page}`;
  handleReplace( { url } );
};

/**
 * handles the authToken request from the child iframe....initiates a DXL action that calls the AmitySocialHub query
 * which returns the latest authToken based on the profileId passed in
 * @param data
 * @param methods
 */
export const handleAuthTokenRequest = ( data, methods ) => {
  const { fetchAuthToken, authTokenProfileId, fetchAuthTokenAction } = data || {};
  const { invokeAction } = methods || {};

  if( !fetchAuthToken ){
    return;
  }

  if( fetchAuthToken ){
    // get another token for the specific profile id
    const authTokenLoginActionWithProfileId = { ...fetchAuthTokenAction,
      variables: { moduleParams: { testKey: Math.random(), profileId: authTokenProfileId, loginStatus: 'anonymous', authTokenRequest: new Date().getTime() } }
    };
    invokeAction( authTokenLoginActionWithProfileId );
  }
};

/**
 * Handles login prompt behavior
 * @param {object} data - Arguments
 * @param {object} methods - Methods
 */
export const handleLoginPrompt = ( data, methods ) => {
  const { hasLoaded, loginAction, shouldShowLogin } = data || {};
  const { invokeAction, setShouldShowLogin } = methods || {};

  if( !hasLoaded || !loginAction || !shouldShowLogin ){
    return;
  }
  const fakeloginAction = { ...loginAction,
    variables: { moduleParams: { testKey: Math.random() } }
  };
  invokeAction( fakeloginAction );
  setShouldShowLogin( false );
};

/**
 * Handles daily prompt timer
 * @param {object} methods - Methods
 */
export const handleDailyPrompt = ( methods ) => {
  const { setDailyPrompt, setShouldShowLogin } = methods || {};

  let lastAmityPrompt = getStorage( { secure: false, key: STORAGE_KEY.amityPrompt } ) || {};

  if( !lastAmityPrompt?.lastPrompt || ( Date.now() - lastAmityPrompt.lastPrompt > 86400000 ) ){
    lastAmityPrompt = Date.now();
    const lastPromptData = { lastPrompt: lastAmityPrompt };
    setStorage( {
      secure: false,
      key: STORAGE_KEY.amityPrompt,
      value: lastPromptData
    } );
    setDailyPrompt( false );
    setShouldShowLogin( true );
  }
};

/**
 * Extracts images and video from a post object.
 * @param {object} post - The post object from the Amity API.
 * @returns {{images: (string|null), videoUrl: (string|null)}}
 */
export const getPostMedia = ( post ) => {
  const media = { videoUrl: null };
  const { postChildren, files } = post;

  const videoChildren =
    postChildren?.filter( ( c ) => c?.dataType === 'video' && c.data?.videoFileId ) ||
    [];

  if( videoChildren.length > 0 ){
    const video = files.find(
      ( file ) => file.type === 'video'
    );
    media.videoUrl = video?.fileUrl || null;
  }

  return media;
};

/**
 * Builds the social object for SEO purposes.
 * @param {object} post - The post object from the Amity API.
 * @returns {object}
 */
export const buildAmitySocialObject = ( post ) => {
  const postData = post?.posts[0];
  const users = post?.users || [];
  const comments = post?.comments || [];
  const { commentsCount, data, reactionsCount, createdAt, postedUserId, postId } = postData;
  const { title, text } = data || {};

  const userCanonicalUrl = postedUserId ? `${window.location.origin}/community/groups?user=${postedUserId}` : '';
  const postCanonicalUrl = postId ? `${window.location.origin}/community/groups?post=${postId}` : '';

  const interactionObject = [
    {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/LikeAction',
      userInteractionCount: reactionsCount
    },
    {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/CommentAction',
      userInteractionCount: commentsCount
    }
  ];

  const userMap = new Map( users.map( ( u ) => [u.userId, u] ) );
  const commentObj = comments.map( ( comment ) => {
    const user = userMap.get( comment.userId );
    const userUrl = user ? `${window.location.origin}/community/groups?user=${user.userId}` : '';
    const commentAuthor = user ?
      {
        '@type': 'Person',
        name: user.displayName,
        url: userUrl
      } : null;

    return {
      '@type': 'Comment',
      author: commentAuthor,
      text: comment.data.text,
      datePublished: comment.createdAt,
      dateModified: comment.updatedAt,
      ...( comment.reactionsCount > 0 && {
        interactionStatistic: {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/LikeAction',
          userInteractionCount: comment?.reactionsCount
        }
      } )
    };
  } );

  const author = users.find( ( u ) => u.userId === postedUserId );

  return {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    headline: title,
    text: text,
    datePublished: createdAt,
    author: {
      '@type': 'Person',
      name: author?.displayName || '',
      url: userCanonicalUrl
    },
    url: postCanonicalUrl,
    interactionStatistic: interactionObject,
    ...( commentObj.length > 0 && { comment: commentObj } )
  };
};

/**
 * Custom hook for managing Amity UI Kit interactions.
 * @param {object} methods - Callbacks for state updates.
 * @returns {object} - The loaded Amity Kit module.
 */
export const useAmityUIKit = ( methods ) => {
  const [AmityKit, setAmityKit] = useState( null );
  const { setDailyPrompt, setHasLoaded, setShouldShowLogin } = methods;

  useEffect( () => {
    // Dynamically import the UI kit to prevent SSR errors.
    import( '@amityco/ulta-ui-kit' ).then( module => {
      setAmityKit( module );
    } );

    const handleIframeMessage = ( event ) => {
      switch ( event.data ){
        case AMITY_EVENT.EXPLORE_PAGE_LOADED:
        case AMITY_EVENT.ON_PAGE_LOADED:
          setDailyPrompt( true );
          setHasLoaded( true );
          break;
        case AMITY_EVENT.ANONYMOUS_NEEDS_LOGIN:
          setShouldShowLogin( true );
          break;
        case AMITY_EVENT.PARENT_NEEDS_SCROLL_TO_TOP:
          window.scrollTo( 0, 0 );
          break;
        default:
          break;
      }
    };

    window.addEventListener( 'message', handleIframeMessage );

    return () => {
      window.removeEventListener( 'message', handleIframeMessage );
    };
  }, [setDailyPrompt, setHasLoaded, setShouldShowLogin] );

  return AmityKit;
};

/**
 * Builds and stringifies the social object for LD+JSON.
 * @param {object} post - The post object.
 * @returns {string|null} - The stringified JSON-LD object or null.
 */
export const getAmitySocialObjectJsonLd = ( post ) => {
  if( isEmptyObject( post ) || !post.posts?.length ){
    return null;
  }
  try {
    const socialObject = utils.buildAmitySocialObject( post );
    return JSON.stringify( socialObject );
  }
  catch ( e ){
    // eslint-disable-next-line no-console
    console.error( 'AmitySocialHub: Failed to stringify social object for LD+JSON', e );
    return null;
  }
};

/**
 * Extracts the video URL from a post object.
 * @param {object} post - The post object.
 * @returns {string|null} - The video URL or null.
 */
export const getAmityVideoUrl = ( post ) => {
  if( isEmptyObject( post ) || !post.files?.length ){
    return null;
  }
  return utils.getPostMedia( post )?.videoUrl || null;
};

/**
 * Property type definitions
 * @typedef AmitySocialHubProps
 * @type {object}
 * @property {string} [profileId] - The user's profile ID.
 * @property {object} loginAction - Action to invoke for login.
 * @property {function} invokeAction - Function to invoke actions.
 * @property {object} fetchAuthTokenAction - Action to fetch an auth token.
 * @property {string} authToken - The current auth token.
 * @property {number} authTokenRequest - Timestamp of the last token request.
 * @property {object} [post] - The post object from the Amity API.
 */
AmitySocialHub.propTypes = {
  profileId: PropTypes.string,
  loginAction: PropTypes.object,
  invokeAction: PropTypes.func,
  fetchAuthTokenAction: PropTypes.object,
  authToken: PropTypes.string,
  authTokenRequest: PropTypes.number,
  post: PropTypes.object
};

export default AmitySocialHub;
